/**
 * lib/pult-presets.ts — the automat's ready-made moves with the effects: PRESET (task 58).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Цепочка звука деки, микшер, автомат»; story
 * 174. A table, and the step it gives for a moment of a transition — pure, no browser: the player
 * reads it on every step of an automatic transition (`engine/player.ts`) and writes what it says
 * on the deck going out, never under a finger.
 *
 * - **`clean`** — nothing: the transition is the crossfader alone.
 * - **`dubout`** — DUB OUT: {@link DUB_OUT_BEATS} beats before the middle of the transition the deck
 *   going out takes ECHO, BEATS of three beats, its FX paddle up, and DEPTH from where it stood to
 *   0,6 by the middle; in the middle, ECHO OUT — the track drowns in its echo.
 *
 * The beats are the outgoing track's own (`lib/pult-effects.ts`, `deckBpm` at speed 1): the
 * transition's progress is reckoned in that track's seconds (`lib/transition.ts`, `frameAt`).
 */

import type { PultEffect } from './pult-effects';

export type PultPreset = 'clean' | 'dubout';

/** DUB OUT starts this many beats before the middle of the transition. */
export const DUB_OUT_BEATS = 4;

/** What a preset sets on the deck going out, from some beats before the middle on. */
interface PresetMove {
  /** From this many beats before the middle… */
  readonly fromBeats: number;
  readonly effect?: PultEffect;
  /** BEATS, in beats. */
  readonly beats?: number;
  /** The FX paddle. */
  readonly lever?: number;
  /** DEPTH: from where it stood at {@link PresetMove.fromBeats} to this at the middle. */
  readonly depthTo?: number;
  /** ECHO OUT, once. */
  readonly echoOut?: boolean;
}

/** The presets' moves, in order. */
const PRESET_MOVES: Readonly<Record<PultPreset, readonly PresetMove[]>> = Object.freeze({
  clean: [],
  dubout: [
    { fromBeats: DUB_OUT_BEATS, effect: 'echo', beats: 3, lever: 1, depthTo: 0.6 },
    { fromBeats: 0, echoOut: true },
  ],
});

/** What the deck going out is to have now. */
export interface PresetStep {
  readonly effect?: PultEffect;
  readonly beats?: number;
  readonly lever?: number;
  readonly depth?: number;
  readonly echoOut: boolean;
}

/**
 * `preset` at `beatsToMiddle` beats before the middle of the transition (0 or less: the middle or
 * after), DEPTH having stood at `depthFrom` when the preset began. `undefined`: nothing yet.
 */
export function presetAt(
  preset: PultPreset,
  beatsToMiddle: number,
  depthFrom: number,
): PresetStep | undefined {
  const due = PRESET_MOVES[preset].filter((move) => beatsToMiddle <= move.fromBeats);
  if (due.length === 0 || !Number.isFinite(beatsToMiddle)) return undefined;
  let step: PresetStep = { echoOut: false };
  for (const move of due) {
    const depth =
      move.depthTo === undefined
        ? step.depth
        : depthFrom +
          (move.depthTo - depthFrom) *
            Math.min(1, Math.max(0, 1 - Math.max(0, beatsToMiddle) / move.fromBeats));
    step = {
      ...step,
      ...(move.effect === undefined ? {} : { effect: move.effect }),
      ...(move.beats === undefined ? {} : { beats: move.beats }),
      ...(move.lever === undefined ? {} : { lever: move.lever }),
      ...(depth === undefined ? {} : { depth }),
      echoOut: step.echoOut || move.echoOut === true,
    };
  }
  return step;
}
